import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import {
	getCommissionContentTitle,
	getDefaultTier,
	getSpecificIdentifier,
	isDefaultVariableGross,
	isMultipleCommissions,
	isOptionsAndTierValid,
	updateCommissionContent,
} from "../utils";

// Custom hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useReferenceState } from "@/Common/hooks/ReferenceState";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";

// Custom components
import CommissionsMatrix from "@/PayPlans/components/Defaults/Managers/Sales/DefaultCommissions/CommissionSection/CommissionsMatrix";
import MiniDealExtension from "@/PayPlans/components/Commons/MiniDealExtension";
import VariableGrossStructure from "@/PayPlans/components/Commons/VariableGrossStructure";

// MUI
import { Grid } from "@mui/material";

// Constants
const INDIVIDUAL_LABEL = "Individual";
const STORE_LABEL = "Store";
const SWITCH_LABEL = "Apply Retroactively";

const isValidMultiple = (validations = []) => validations.every((isValid) => isValid);
const isValidMultipleStructure = (grossStructure = [], commissions = []) =>
	commissions.every((commission) => isOptionsAndTierValid(grossStructure, commission, commission.make));

const RenderCommissionContent = (props) => {
	const {
		identifier = "",
		commissions = null,
		grossStructure = [],
		readOnly = false,
		onActionUpdate = () => {},
		onActionUpdateSwitches = () => {},
		onActionValidation = () => {},
	} = props;

	const multipleValid = useRef([]);

	// Check for single or multiple commissions
	const isMultiple = isMultipleCommissions(commissions);

	const handleUpdateTiers = (updatedTiers, isValid, index = -1) => {
		const updated = updateCommissionContent(commissions, updatedTiers, index);

		const isValidOptions = isMultiple
			? isValidMultipleStructure(grossStructure, updated)
			: isOptionsAndTierValid(grossStructure, updated);

		if (index !== -1) {
			multipleValid.current[index] = isValid;
			isValid = isValidMultiple(multipleValid.current);
		}

		const isAllValid = isValid && isValidOptions;
		onActionUpdate?.(updated, isAllValid);
	};

	const handleValidation = (isValid, index = -1) => {
		if (index !== -1) {
			multipleValid.current[index] = isValid;
			isValid = isValidMultiple(multipleValid.current);
		}
		onActionValidation?.(isValid);
	};

	if (!isMultiple) {
		const { make = "", tiers = [], applyTiersRetroactively = false, individual = false } = commissions || {};
		return (
			<Grid container item xs={12}>
				<CommissionsMatrix
					title={getCommissionContentTitle(identifier)}
					make={make}
					dealerCodeFS={""}
					tiers={tiers || []}
					crossStore={true}
					applyTiersRetroactively={applyTiersRetroactively}
					individual={individual}
					finance={Boolean(identifier === "finance")}
					grossError={!isOptionsAndTierValid(grossStructure, commissions)}
					hideFinanceActions={true}
					readOnly={readOnly || isDefaultVariableGross(grossStructure)}
					onActionValidation={(_make, _dealerCodeFS, isValid) => handleValidation(isValid)}
					onActionUpdate={(_make, _dealerCodeFS, updatedTiers, isValid) => handleUpdateTiers(updatedTiers, isValid)}
				/>
			</Grid>
		);
	}

	return (
		<Grid container item xs={12}>
			{commissions?.map((commission, index) => {
				const { make = "", tiers = [], applyTiersRetroactively = false, individual = false } = commission || {};
				const specificIdentifier = getSpecificIdentifier(make);
				const title = getCommissionContentTitle(identifier, specificIdentifier);
				const isOptionsAndTiersValid = isOptionsAndTierValid(grossStructure, commission, make);
				const marginTop = index > 0 ? 4 : 0;
				return (
					<Grid key={`${title}_${index}`} container item xs={12} mt={marginTop}>
						<CommissionsMatrix
							title={title}
							make={make}
							dealerCodeFS={""}
							tiers={tiers || []}
							crossStore={true}
							customizedActions={[
								<CustomizedSwitches
									key={"individual"}
									textLeft={true}
									textChecked={STORE_LABEL}
									textUnChecked={INDIVIDUAL_LABEL}
									checked={Boolean(individual)}
									disabled={readOnly}
									onChange={(value) => onActionUpdateSwitches?.("individual", value, index)}
								/>,
								<CustomizedSwitches
									key={"applyTiersRetroactively"}
									textLeft={true}
									textChecked={SWITCH_LABEL}
									textUnChecked={SWITCH_LABEL}
									checked={Boolean(applyTiersRetroactively)}
									disabled={readOnly}
									onChange={(value) => onActionUpdateSwitches?.("applyTiersRetroactively", value, index)}
								/>,
							]}
							finance={Boolean(identifier === "finance")}
							grossError={!isOptionsAndTiersValid}
							hideFinanceActions={true}
							readOnly={readOnly || isDefaultVariableGross(grossStructure, make)}
							onActionValidation={(_make, _dealerCodeFS, isValid) =>
								handleValidation(isValid && isOptionsAndTiersValid, index)
							}
							onActionUpdate={(_make, _dealerCodeFS, updatedTiers, isValid) =>
								handleUpdateTiers(updatedTiers, isValid, index)
							}
						/>
					</Grid>
				);
			})}
		</Grid>
	);
};
RenderCommissionContent.propTypes = {
	identifier: PropTypes.string,
	commissions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
	grossStructure: PropTypes.array,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionUpdateSwitches: PropTypes.func,
	onActionValidation: PropTypes.func,
};

export default function CommissionContent(props) {
	const {
		identifier = "",
		content = null,
		brands = [],
		errors = false,
		loadingMakes = false,
		readOnly = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Control to send update actions
	const wasUpdated = useRef(false);

	const updateContent = (updatedData) => {
		onActionValidation?.(isValidModel);
		if (wasUpdated.current) {
			onActionUpdate?.(identifier, updatedData);
		}
	};

	const [isValidAll, setIsValidAll, validAll, recheckValidAll] = useReferenceState(true, []);
	const [innerContent, setInnerContent] = useCallbackState(null, updateContent);
	const { commissions, variableGrossStructure = [] } = innerContent || {};

	// Check for single or multiple commissions
	const isMultiple = isMultipleCommissions(commissions);
	const columnsGroup = isMultiple
		? [
				{ match: "New F&I", includes: true },
				{ match: "Used F&I", includes: true },
		  ]
		: [];

	const isValidModel = useMemo(() => isValidAll, [isValidAll]);

	const handleUpdateSwitches = (area, value, index = -1) => {
		const updated = { ...innerContent };
		updated["commissions"][index][area] = value;
		wasUpdated.current = !errors;
		setInnerContent(updated);
	};

	const handleUpdateTiers = (updatedCommissions, isValid = false) => {
		const updated = { ...innerContent, commissions: updatedCommissions };
		wasUpdated.current = isValidModel;
		setInnerContent(updated);
	};

	const handleUpdateMiniDeal = (updatedMiniDeal) => {
		const updated = { ...innerContent, miniDeal: updatedMiniDeal };
		wasUpdated.current = isValidModel;
		setInnerContent(updated);
	};

	const handleUpdateGrossStructure = (values) => {
		const updated = { ...innerContent, variableGrossStructure: values };
		const isValidOptions = isMultiple ? isValidMultipleStructure(values, commissions) : isOptionsAndTierValid(values, commissions);
		if (isDefaultVariableGross(values)) {
			const prevCommissions = updated["commissions"];
			if (isMultiple) {
				updated["commissions"] = prevCommissions.map((commission) => ({ ...commission, tiers: [getDefaultTier()] }));
			} else {
				updated["commissions"] = { ...prevCommissions, tiers: [getDefaultTier()] };
			}
		}
		wasUpdated.current = isValidOptions;
		setInnerContent(updated);
	};

	useEffect(() => {
		setInnerContent(clone(content));
		wasUpdated.current = false;

		return () => {
			setIsValidAll(true);
			setInnerContent(null);
		};
	}, [content, setInnerContent]);

	return (
		<Grid container>
			<RenderCommissionContent
				identifier={identifier}
				commissions={commissions}
				grossStructure={variableGrossStructure}
				readOnly={readOnly}
				onActionUpdate={(updatedCommissions, isValid) => handleUpdateTiers(updatedCommissions)}
				onActionValidation={(isValid) => {
					validAll[0] = isValid;
					recheckValidAll();
				}}
				onActionUpdateSwitches={handleUpdateSwitches}
			/>

			{Boolean("miniDeal" in (innerContent ?? {})) && (
				<Grid item xs={12} mt={2}>
					<MiniDealExtension
						identifier={identifier}
						miniDeals={innerContent?.miniDeal ?? []}
						brands={brands ?? []}
						isSpecificModelsAllowed={identifier === "preOwned"}
						loading={loadingMakes}
						readOnly={readOnly || loadingMakes}
						onActionUpdate={(deals, isValid) => handleUpdateMiniDeal(deals, isValid)}
						onActionValidation={(isValid) => {
							validAll[1] = isValid;
							recheckValidAll();
						}}
					/>
				</Grid>
			)}

			{Boolean(variableGrossStructure?.length) && (
				<Grid container item mt={2} xs={12}>
					<VariableGrossStructure
						showBy={"make"}
						columns={columnsGroup}
						grossStructure={variableGrossStructure || []}
						message={"Highest commission must be not equal to zero."}
						readOnly={readOnly}
						onActionUpdate={handleUpdateGrossStructure}
					/>
				</Grid>
			)}
		</Grid>
	);
}
CommissionContent.propTypes = {
	identifier: PropTypes.string,
	content: PropTypes.shape({
		commissions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
		variableGrossStructure: PropTypes.array,
	}),
	errors: PropTypes.bool,
	loadingMakes: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
